import { map, option } from 'fp-ts';
import * as F from 'fp-ts/function';
export { not, or } from 'fp-ts/Predicate';
export const absurd = F.absurd;
export const constant = F.constant;
export const constFalse = F.constFalse;
export const constNull = F.constNull;
export const constTrue = F.constTrue;
export const constUndefined = F.constUndefined;
export const constVoid = F.constVoid;
export const identity = F.identity;
export const increment = F.increment;
export const decrement = F.decrement;
export const flow = F.flow;
export const flow2 = (...[fn, ...fns]) => (...a) => (...b) => fns.reduce((val, fn) => fn(val), fn(...a)(...b));
export const flow3 = (...[fn, ...fns]) => (...a) => (...b) => (...c) => fns.reduce((val, fn) => fn(val), fn(...a)(...b)(...c));
/**
 * Pipe is used to create transformation pipelines for the values we work with.
 * It works similar to a fluent interface we know from jQuery but composes
 * functions instead. Due to the way TypeScript’s type inference works, pipe
 * works from left to right, i.e. the first argument determines the initial
 * type that will then be transformed in the rest of the pipeline.
 *
 * @example
 * const four = pipe(
 *   2,
 *   (x) => x * x
 * )
 */
export const pipe = F.pipe;
const tryCatchK = (fn, onThrow) => (a) => {
    try {
        return fn(a);
    }
    catch (e) {
        return onThrow(e);
    }
};
const tryCatchKW = (fn, onThrow) => (a) => {
    try {
        return fn(a);
    }
    catch (e) {
        return onThrow(e);
    }
};
/**
 * Copied from fp-ts-std
 *
 * Given a function and an `Eq` instance for determining input equivalence,
 * returns a new function that caches the result of applying an input to said
 * function. If the cache hits, the cached value is returned and the function
 * is not called again. Useful for expensive computations.
 *
 * Provided the input function is pure, this function is too.
 *
 * The cache is implemented as a simple `Map`. There is no mechanism by which
 * cache entries can be cleared from memory.
 *
 * @example
 * import { memoize } from 'fp-ts-std/Function'
 * import { add } from 'fp-ts-std/Number'
 * import { eqNumber } from 'fp-ts/Eq'
 *
 * let runs = 0
 * const f = memoize(eqNumber)(n => {
 *     runs++
 *     return add(5)(n)
 * })
 *
 * assert.strictEqual(runs, 0)
 * assert.strictEqual(f(2), 7)
 * assert.strictEqual(runs, 1)
 * assert.strictEqual(f(2), 7)
 * assert.strictEqual(runs, 1)
 */
export const memoize = (eq) => (f) => {
    const cache = new Map();
    return (k) => {
        const cached = map.lookup(eq)(k)(cache);
        if (option.isSome(cached))
            return cached.value;
        const val = f(k);
        cache.set(k, val);
        return val;
    };
};
/**
 * Lazily compute a value. The function f is called at most once.
 * @param f the function to compute the value
 */
export const lazy = (f) => {
    let a;
    let called = false; // We use a separate variable in case f returns null or undefined
    return () => {
        if (!called) {
            a = f();
            called = true;
        }
        return a;
    };
};
/**
 * Equivalent to `flip`, but supports variadic arguments.
 */
export const flipVariadic = (f) => (...b) => (...a) => f(...a)(...b);
export const fn = {
    apply: F.apply,
    tuple: F.tuple,
    tupled: F.tupled,
    untupled: F.untupled,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    flip: F.flip,
    flipVariadic,
    tryCatchK,
    tryCatchKW,
    memoize,
};
/**
 * Replacement for ['a', 'b'].includes(someVariable), but better typed.
 * Does not accept type `string` in as values ("haystack").
 */
export const isOneOf = (...values) => (value) => values.includes(value);
//# sourceMappingURL=function.js.map